import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { projectsAPI, reportsAPI } from '../services/api';
import { 
  BarChart3, 
  Download, 
  Building2,
  DollarSign,
  TrendingUp,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchSummary();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const projects = response?.data?.data || response?.data || [];
      setProjects(projects);
      if (projects.length > 0) {
        setSelectedProject(projects[0].id);
      }
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    setLoadingSummary(true);
    try {
      const response = await reportsAPI.getProjectSummary(selectedProject);
      const summary = response?.data?.data || response?.data;
      setSummary(summary);
    } catch (error) {
      console.error('Error al cargar resumen:', error);
      toast.error('Error al cargar resumen');
    } finally {
      setLoadingSummary(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await reportsAPI.downloadProjectExcel(selectedProject);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_${summary?.project?.name || 'obra'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte Excel descargado');
    } catch (error) {
      toast.error('Error al descargar Excel');
    }
  };

  const downloadPDF = () => {
    if (!summary) return;

    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(192, 86, 33);
    doc.rect(0, 0, 210, 45, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text('BROCO', 20, 28);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Reporte de Obra', 20, 38);
    
    // Project name
    doc.setTextColor(45, 42, 38);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(summary.project.name, 20, 60);
    
    // Project info
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(92, 85, 75);
    doc.text(`Estado: ${summary.project.status}`, 20, 70);
    doc.text(`Ubicación: ${summary.project.location || 'No especificada'}`, 20, 77);
    doc.text(`Periodo: ${summary.project.start_date || 'N/A'} - ${summary.project.end_date || 'N/A'}`, 20, 84);
    
    // Budget section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(45, 42, 38);
    doc.text('Resumen Financiero', 20, 100);
    
    doc.autoTable({
      startY: 105,
      head: [['Concepto', 'Monto']],
      body: [
        ['Presupuesto Total', `$${summary.budget.total.toLocaleString('es-MX')}`],
        ['Ejecutado', `$${summary.budget.executed.toLocaleString('es-MX')}`],
        ['Restante', `$${summary.budget.remaining.toLocaleString('es-MX')}`],
        ['Avance', `${summary.budget.percentage}%`]
      ],
      theme: 'grid',
      headStyles: { fillColor: [192, 86, 33], textColor: 255 },
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: 'right' } }
    });
    
    // Progress section
    const afterBudget = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Estadísticas de Avances', 20, afterBudget);
    
    doc.autoTable({
      startY: afterBudget + 5,
      head: [['Estado', 'Cantidad']],
      body: [
        ['Total registrados', summary.progress.total],
        ['Pendientes', summary.progress.pending],
        ['Aprobados', summary.progress.approved],
        ['Rechazados', summary.progress.rejected]
      ],
      theme: 'grid',
      headStyles: { fillColor: [129, 161, 131], textColor: 255 },
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: 'center' } }
    });
    
    // Quotes section
    const afterProgress = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Estadísticas de Cotizaciones', 20, afterProgress);
    
    doc.autoTable({
      startY: afterProgress + 5,
      head: [['Estado', 'Cantidad']],
      body: [
        ['Total recibidas', summary.quotes.total],
        ['Pendientes', summary.quotes.pending],
        ['Aceptadas', summary.quotes.accepted],
        ['Rechazadas', summary.quotes.rejected]
      ],
      theme: 'grid',
      headStyles: { fillColor: [237, 137, 54], textColor: 255 },
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: 'center' } }
    });
    
    // Catalog info
    const afterQuotes = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Información del Catálogo', 20, afterQuotes);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total de partidas: ${summary.catalog.total_items}`, 20, afterQuotes + 10);
    if (summary.catalog.categories.length > 0) {
      doc.text(`Categorías: ${summary.catalog.categories.join(', ')}`, 20, afterQuotes + 17);
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(`Generado el: ${new Date().toLocaleString('es-MX')}`, 20, 285);
    doc.text('BROCO - ERP de Obra', 150, 285);
    
    doc.save(`reporte_${summary.project.name}.pdf`);
    toast.success('PDF generado');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="reports-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">Reportes</h1>
            <p className="text-[#5C554B]">Genera reportes y dashboards de tus obras</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={downloadExcel} 
              variant="outline" 
              className="btn-secondary"
              disabled={!selectedProject || loadingSummary}
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button 
              onClick={downloadPDF} 
              className="btn-primary"
              disabled={!summary || loadingSummary}
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Project Selector */}
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <Label className="mb-2 block">Seleccionar Obra</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="h-12 bg-white max-w-md">
                <SelectValue placeholder="Selecciona una obra" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {loadingSummary ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        ) : summary ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="card-dashboard">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#C05621]/10 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-[#C05621]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#5C554B]">Presupuesto</p>
                      <p className="font-bold text-[#2D2A26]">{formatCurrency(summary.budget.total)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-dashboard">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#81A183]/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-[#81A183]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#5C554B]">Ejecutado</p>
                      <p className="font-bold text-[#2D2A26]">{formatCurrency(summary.budget.executed)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-dashboard">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[#5C554B]">Partidas</p>
                      <p className="font-bold text-[#2D2A26]">{summary.catalog.total_items}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-dashboard">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[#5C554B]">Avance</p>
                      <p className="font-bold text-[#2D2A26]">{summary.budget.percentage}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Progress Bar */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle>Avance del Presupuesto</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Progress value={summary.budget.percentage} className="h-4" />
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-[#2D2A26]">{formatCurrency(summary.budget.total)}</p>
                      <p className="text-sm text-[#5C554B]">Total</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#81A183]">{formatCurrency(summary.budget.executed)}</p>
                      <p className="text-sm text-[#5C554B]">Ejecutado</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#C05621]">{formatCurrency(summary.budget.remaining)}</p>
                      <p className="text-sm text-[#5C554B]">Restante</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Statistics Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle>Avances de Obra</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[#5C554B]">Total</span>
                      <span className="font-bold text-[#2D2A26]">{summary.progress.total}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#5C554B]">Pendientes</span>
                      <span className="font-bold text-amber-600">{summary.progress.pending}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#5C554B]">Aprobados</span>
                      <span className="font-bold text-green-600">{summary.progress.approved}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#5C554B]">Rechazados</span>
                      <span className="font-bold text-red-600">{summary.progress.rejected}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle>Cotizaciones</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[#5C554B]">Total</span>
                      <span className="font-bold text-[#2D2A26]">{summary.quotes.total}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#5C554B]">Pendientes</span>
                      <span className="font-bold text-amber-600">{summary.quotes.pending}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#5C554B]">Aceptadas</span>
                      <span className="font-bold text-green-600">{summary.quotes.accepted}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#5C554B]">Rechazadas</span>
                      <span className="font-bold text-red-600">{summary.quotes.rejected}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <Card className="card-dashboard">
            <CardContent className="py-12 text-center">
              <BarChart3 className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#2D2A26] mb-2">Selecciona una obra</h3>
              <p className="text-[#5C554B]">Elige un proyecto para ver su reporte</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Reports;
